import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  useDeleteCustomerServiceRep,
  useReassignCustomerServiceRep,
} from '~/hooks/apis/loyalty/useCustomerServiceRepMutations';

import type { CustomerServiceRep } from '~/services/LuxLoyalty';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Rep to remove. */
  rep: CustomerServiceRep | null;
  /** Other reps available as reassignment targets. */
  otherReps: ReadonlyArray<CustomerServiceRep>;
  /** Number of customers currently mapped to this rep. When 0 we skip the reassign step. */
  mappedCustomerCount: number;
}

/**
 * Confirmation dialog used to remove a rep. When the rep has 0 mapped
 * customers we show a simple confirm; otherwise we require an explicit
 * reassignment target and chain a bulk reassign before delete.
 */
export default function CustomerServiceRepRemoveDialog({ open, onClose, rep, otherReps, mappedCustomerCount }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [reassignToId, setReassignToId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReassignToId(null);
    }
  }, [open]);

  const reassignMutation = useReassignCustomerServiceRep({
    onError: (error) => {
      enqueueSnackbar(`Failed to reassign customers: ${error.message}`, { variant: 'error' });
    },
  });

  const deleteMutation = useDeleteCustomerServiceRep({
    onSuccess: () => {
      enqueueSnackbar('Representative removed', { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to remove rep: ${error.message}`, { variant: 'error' });
    },
  });

  const isSubmitting = reassignMutation.isPending || deleteMutation.isPending;
  const needsReassign = mappedCustomerCount > 0;
  const canSubmit = rep !== null && !isSubmitting && (!needsReassign || !!reassignToId);

  const handleSubmit = async () => {
    if (!rep) {
      return;
    }
    try {
      if (needsReassign && reassignToId) {
        await reassignMutation.mutateAsync({ fromRepId: rep.id, toRepId: reassignToId });
      }
      await deleteMutation.mutateAsync(rep.id);
    } catch {
      // Errors are surfaced via the mutation onError handlers.
    }
  };

  if (!rep) {
    return null;
  }

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Remove {rep.firstName} {rep.lastName}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            This representative will be permanently removed from the configurator.
          </Typography>

          {needsReassign && (
            <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2">
                <strong>
                  {rep.firstName} {rep.lastName}
                </strong>{' '}
                is the dedicated concierge for{' '}
                <strong>
                  {mappedCustomerCount} member{mappedCustomerCount === 1 ? '' : 's'}
                </strong>
                . Choose a representative to take over the portfolio before removal.
              </Typography>
            </Alert>
          )}

          {needsReassign && (
            <Box>
              <TextField
                select
                label={`Reassign ${mappedCustomerCount} member${mappedCustomerCount === 1 ? '' : 's'} to`}
                value={reassignToId ?? ''}
                onChange={(e) => setReassignToId(e.target.value ? String(e.target.value) : null)}
                fullWidth
                disabled={isSubmitting || otherReps.length === 0}
                helperText={
                  otherReps.length === 0
                    ? 'Add another rep before removing this one.'
                    : 'The new rep will inherit every mapped customer.'
                }
              >
                {otherReps.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    {option.firstName} {option.lastName} — {option.email}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {needsReassign ? 'Reassign & remove' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
